import React from 'react';
import Button from './Button';
import ArrowLeftIcon from './CustomIcons/ArrowLeftIcon';
import CaretRightIcon from './CustomIcons/CaretRightIcon';
import type { BreadcrumbLink, PageAction, RenderBreadcrumbLink } from './PageLayout/types';
import Text, { type TextColor } from './Typography/Text';
import { hasBackButton } from './breadcrumbsNav';
import { FOCUS_RING } from './focusRing';
import { isSafeHref, safeHref } from './safeHref';

export interface BreadcrumbsProps {
    links: BreadcrumbLink[];
    actions?: PageAction[];
    renderLink?: RenderBreadcrumbLink;
    className?: string;
    style?: React.CSSProperties;
}

const CRUMB_LINK_CLASSES = `no-underline rounded-xs cursor-pointer transition-colors duration-200 ease-in-out hover:text-[var(--malachite-accent-foreground)] ${FOCUS_RING}`;

const BACK_BUTTON_CLASSES = `flex h-6 w-6 shrink-0 items-center justify-center rounded-[var(--malachite-radius-10,10px)] no-underline bg-[var(--malachite-number-tag-background)] text-[var(--malachite-text-primary)] transition-colors duration-200 ease-in-out hover:bg-[var(--malachite-number-tag-emphasis-background)] ${FOCUS_RING}`;

type LinkProps = Parameters<RenderBreadcrumbLink>[0];

const renderCrumbLink = (props: LinkProps, renderLink?: RenderBreadcrumbLink): React.ReactNode => {
    // An unsafe url degrades to plain text rather than a link that could run a script.
    if (!isSafeHref(props.href)) {
        return props.children;
    }
    return renderLink ? renderLink(props) : <a {...props} />;
};

const renderCrumbName = (name: React.ReactNode, color: TextColor): React.ReactNode =>
    typeof name === 'string' ? (
        <Text size="medium" fontWeight="semi-bold" color={color}>
            {name}
        </Text>
    ) : (
        name
    );

const renderAction = (
    {
        label,
        onClick,
        disabled,
        variant,
        icon,
        href,
        target,
        tooltip,
        tooltipPlacement,
    }: PageAction,
    index: number,
) => {
    const safe = safeHref(href);
    return (
        <Button
            key={`${index}-${label}`}
            size="small"
            variant={variant ?? 'outline'}
            disabled={disabled}
            icon={icon}
            tooltip={tooltip}
            tooltipPlacement={tooltipPlacement}
            href={safe}
            target={safe ? (target ?? '_blank') : undefined}
            onClick={safe ? undefined : onClick}
        >
            {label}
        </Button>
    );
};

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
    links,
    actions,
    renderLink,
    className = '',
    style,
}) => {
    // PageLayout mounts this unconditionally, so returning markup would leave an empty
    // navigation landmark on every page without crumbs.
    if (links.length === 0 && !actions?.length) {
        return null;
    }

    const isTitle = links.length === 1 && Boolean(links[0].isRoot);
    const parentCrumb = hasBackButton(links) ? links[links.length - 2] : undefined;
    const parentName = parentCrumb?.name;
    const hasActions = Boolean(actions?.length);

    return (
        <div
            className={`flex items-center ${hasActions ? 'justify-between' : ''} ${className}`}
            style={style}
        >
            {isTitle ? (
                renderCrumbName(links[0].name, 'primary')
            ) : (
                <div className="flex items-center gap-4">
                    {parentCrumb?.url &&
                        renderCrumbLink(
                            {
                                href: parentCrumb.url,
                                'aria-label':
                                    typeof parentName === 'string'
                                        ? `Back to ${parentName}`
                                        : 'Go back',
                                className: BACK_BUTTON_CLASSES,
                                children: <ArrowLeftIcon size={12} />,
                            },
                            renderLink,
                        )}
                    {links.length > 0 && (
                        <nav aria-label="Breadcrumb">
                            <ol className="flex flex-wrap items-center gap-2 m-0 p-0 list-none">
                                {links.map((link, index) => {
                                    const isLast = index === links.length - 1;
                                    const color: TextColor = isLast ? 'primary' : 'muted';

                                    return (
                                        <li
                                            key={`${link.url ?? ''}-${index}`}
                                            className="flex items-center gap-2"
                                            aria-current={isLast && !link.url ? 'page' : undefined}
                                        >
                                            {link.url
                                                ? renderCrumbLink(
                                                      {
                                                          href: link.url,
                                                          'aria-current': isLast
                                                              ? 'page'
                                                              : undefined,
                                                          className: `${CRUMB_LINK_CLASSES} ${
                                                              isLast
                                                                  ? 'text-[var(--malachite-text-primary)]'
                                                                  : 'text-[var(--malachite-text-muted)]'
                                                          }`,
                                                          children: renderCrumbName(
                                                              link.name,
                                                              'inherit',
                                                          ),
                                                      },
                                                      renderLink,
                                                  )
                                                : renderCrumbName(link.name, color)}
                                            {!isLast && (
                                                <span
                                                    aria-hidden="true"
                                                    className="flex items-center text-[var(--malachite-text-dimmed)]"
                                                >
                                                    <CaretRightIcon size={13} />
                                                </span>
                                            )}
                                        </li>
                                    );
                                })}
                            </ol>
                        </nav>
                    )}
                </div>
            )}
            {hasActions && (
                <div className="flex items-center gap-2">{actions?.map(renderAction)}</div>
            )}
        </div>
    );
};

export { Breadcrumbs };
export default Breadcrumbs;
